/**
 * ROUTE TABLE — APPEND-ONLY MERGE POINT (BUILD-PLAN: one of the four).
 *
 * ═══════════════════════ HOW A VERTICAL ADDS A SCREEN ═══════════════════════
 *  1. Create your feature dir:      src/features/<domain>/<screen>.tsx
 *     (screens + components + hooks + css live there — single-owner, never
 *     edit another vertical's dir or the design system).
 *  2. Import your screen below and APPEND ONE ENTRY to `publicRoutes` (no
 *     auth) or `appRoutes` (rendered inside the authenticated TabFrame).
 *     To take over a foundation placeholder (deck/likes/matches/store/me…),
 *     REPLACE that placeholder's `element` in place — same path, your screen.
 *     Never reorder or delete someone else's line; merge conflicts here are
 *     ordering-only and resolved by keeping all lines.
 *  3. Data: REST via `api` (src/lib/api.ts — typed against openapi; wrap
 *     state-machine mutations with `withEventId(...)`), realtime via `ws`
 *     (src/lib/ws.ts — `ws.on("msg.new", …)` returns an unsubscribe; call
 *     `ws.ensureConnected()` on realtime screens; pushes are re-render hints,
 *     REST is the recovery plane). Shared state: `createStore`/`useStore`
 *     (src/lib/store.ts) inside your feature dir.
 *  4. Copy: import { copy, resolveCopy } from "@humble/shared" — never
 *     hardcode © strings. Design primitives from src/design/index.js.
 *
 *  Example append (V7 discovery-likes):
 *    import { DeckScreen } from "./features/discovery/deck.js";
 *    // in appRoutes, replace the placeholder element:
 *    { path: "/deck", element: <DeckScreen /> },
 * ════════════════════════════════════════════════════════════════════════════
 */
import type { ReactElement } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { DesignGallery } from "./design/gallery.js";
import { LandingScreen } from "./features/landing/landing.js";
import { NotFoundScreen } from "./features/shell/placeholder.js";
import { TabFrame } from "./features/shell/tab-frame.js";
import { BannedScreen } from "./features/safety/banned.js";
import { BlocklistScreen } from "./features/safety/blocklist.js";
import { HiddenWordsScreen } from "./features/safety/hidden-words.js";
import { NotificationSettingsScreen } from "./features/notifications/settings.js";
import { AdminScreen } from "./features/admin/admin.js";
import { KillstreakScreen } from "./features/killstreak/killstreak.js";
import { TakeoverScreen } from "./features/takeover/takeover.js";
import { CooldownScreen } from "./features/cooldown/cooldown.js";
import { ShieldScreen } from "./features/shield/shield.js";
import { StoreScreen } from "./features/store/store.js";
import { StoreCancelScreen, StoreSuccessScreen } from "./features/store/result.js";
import { BillingScreen } from "./features/store/billing.js";
import { RouletteScreen } from "./features/roulette/roulette.js";
import { CallScreen } from "./features/call/call-screen.js";
import { BerserkerScreen } from "./features/berserker/berserker.js";
import { MatchesScreen } from "./features/conversations/matches.js";
import { ConversationScreen } from "./features/chat/conversation.js";
import { StatsScreen } from "./features/stats/stats.js";
import { LeaderboardScreen } from "./features/stats/leaderboard.js";
import { DeckScreen } from "./features/deck/deck.js";
import { MenuScreen } from "./features/menu/menu.js";
import { FeatureTogglesScreen } from "./features/settings/features.js";
import { LikesScreen } from "./features/likes/likes.js";
import { DiscoveryFiltersScreen } from "./features/filters/filters.js";
import { AgeGateScreen } from "./features/auth/age-gate.js";
import { SignupAccountScreen } from "./features/auth/account.js";
import { VerifyEmailScreen } from "./features/auth/verify-email.js";
import { MagicLinkScreen } from "./features/auth/magic-link.js";
import { GoogleCallbackScreen } from "./features/auth/google-callback.js";
import { IdentityScreen } from "./features/onboarding/identity.js";
import { ProfileSetupScreen } from "./features/onboarding/profile-setup.js";
import { LocationScreen } from "./features/onboarding/location.js";
import { BriefingScreen } from "./features/onboarding/briefing.js";
import { GraceScreen } from "./features/account/grace.js";
import { MeScreen } from "./features/profile/me.js";
import { SafetyCenterScreen } from "./features/safety/safety-center.js";
import { TermsScreen } from "./features/legal/terms.js";
import { PrivacyScreen } from "./features/legal/privacy.js";

export interface AppRouteEntry {
  path: string;
  element: ReactElement;
}

/** Routes OUTSIDE the authenticated frame (landing, signup/onboarding flow,
 *  forced states, the QA gallery). APPEND-ONLY. */
export const publicRoutes: AppRouteEntry[] = [
  { path: "/", element: <LandingScreen /> }, // S-01 — FOUNDATION (final)
  { path: "/_design", element: <DesignGallery /> }, // QA gallery — FOUNDATION (final)
  // --- V1 auth-profiles-gdpr screens (S-02…S-09, S-30/33, legal):
  { path: "/signup/age", element: <AgeGateScreen /> }, // S-02 (+S-03 lockout) — V1
  { path: "/signup/account", element: <SignupAccountScreen /> }, // S-04 — V1
  { path: "/verify-email", element: <VerifyEmailScreen /> }, // S-05 pending + ?token consume — V1
  { path: "/onboard/identity", element: <IdentityScreen /> }, // S-06 — V1
  { path: "/onboard/profile", element: <ProfileSetupScreen /> }, // S-07 — V1
  { path: "/onboard/location", element: <LocationScreen /> }, // S-08 — V1
  { path: "/onboard/briefing", element: <BriefingScreen /> }, // S-09 — V1
  { path: "/grace", element: <GraceScreen /> }, // S-30 grace login state — V1
  { path: "/banned", element: <BannedScreen /> }, // S-31 — V8 moderation-admin
  { path: "/safety", element: <SafetyCenterScreen /> }, // S-33 — V1
  { path: "/legal/terms", element: <TermsScreen /> }, // S-27 About/legal — V1
  { path: "/legal/privacy", element: <PrivacyScreen /> }, // S-27 About/legal — V1
  { path: "/magic-link", element: <MagicLinkScreen /> }, // emailed sign-in link consume (#80b) — V1
  { path: "/auth/google/callback", element: <GoogleCallbackScreen /> }, // Google PKCE return (#80a) — V1
];

/** Routes INSIDE the authenticated TabFrame (auth-gated; tab bar visible).
 *  APPEND-ONLY — verticals replace placeholder elements / append new paths. */
export const appRoutes: AppRouteEntry[] = [
  { path: "/deck", element: <DeckScreen /> }, // S-10 — V7 discovery-likes
  { path: "/likes", element: <LikesScreen /> }, // S-13 Fans & Haters — V7 discovery-likes
  { path: "/matches", element: <MatchesScreen /> }, // S-15 — V3a chat-fuse
  { path: "/store", element: <StoreScreen /> }, // S-19 — V5 payments
  { path: "/me", element: <MeScreen /> }, // S-25 own profile + edit — V1
  { path: "/settings/blocklist", element: <BlocklistScreen /> }, // S-27 Privacy › Blocklist — V8
  { path: "/settings/hidden-words", element: <HiddenWordsScreen /> }, // S-27 Privacy › Hidden Words — V8
  { path: "/settings/notifications", element: <NotificationSettingsScreen /> }, // S-27 Notifications — V8
  { path: "/admin", element: <AdminScreen /> }, // internal mod panel (requireStaff server-side) — V8
  { path: "/killstreak", element: <KillstreakScreen /> }, // S-22 ladder + token slot — V4
  { path: "/takeover/:takeoverId", element: <TakeoverScreen /> }, // S-23 suite — V4
  { path: "/cooldown", element: <CooldownScreen /> }, // S-26 super-rejected state — V2
  { path: "/shield", element: <ShieldScreen /> }, // S-21 shield card + arm UI — V2
  { path: "/store/success", element: <StoreSuccessScreen /> }, // S-20 — V5 payments (EC-27 cosmetic)
  { path: "/store/cancel", element: <StoreCancelScreen /> }, // S-20 — V5 payments
  { path: "/settings/billing", element: <BillingScreen /> }, // S-27 › Premium & billing — V5 payments
  { path: "/roulette", element: <RouletteScreen /> }, // S-18 the pit — V6 roulette
  { path: "/call", element: <CallScreen /> }, // SM-9 call UI (in-conversation entry: /call?match=&kind=) — V3b
  { path: "/berserker", element: <BerserkerScreen /> }, // S-24 berserker activate + HUD — V3b
  { path: "/matches/:matchId", element: <ConversationScreen /> }, // S-16 — V3a chat-fuse
  { path: "/stats", element: <StatsScreen /> }, // S-32 "Your numbers" — V9 stats-social
  { path: "/stats/leaderboard", element: <LeaderboardScreen /> }, // S-32 leaderboard — V9 stats-social
  { path: "/menu", element: <MenuScreen /> }, // S-14 Today's Menu — V7 discovery-likes
  { path: "/settings/discovery", element: <DiscoveryFiltersScreen /> }, // S-27 Discovery — V7 discovery-likes
  { path: "/settings/features", element: <FeatureTogglesScreen /> }, // S-27 Game features — per-user mechanic toggles (0002)
];

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {publicRoutes.map((r) => (
          <Route key={r.path} path={r.path} element={r.element} />
        ))}
        <Route element={<TabFrame />}>
          {appRoutes.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
        </Route>
        <Route path="*" element={<NotFoundScreen />} />
      </Routes>
    </BrowserRouter>
  );
}
